import React from "react";
import { LegalLayout } from "../../components/LegalLayout";
import { LEGAL } from "../../lib/legal";

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" testid="legal-privacy-page">
      <p>
        This Privacy Policy explains how {LEGAL.companyName} collects, uses, and protects information when you use the
        Service. We are committed to protecting your privacy and complying with applicable laws including the California
        Consumer Privacy Act (CCPA) and, where applicable, the EU General Data Protection Regulation (GDPR).
      </p>

      <h2>1. Information We Collect</h2>
      <h3>You provide directly</h3>
      <ul>
        <li><strong>Account info:</strong> name, email address, hashed password.</li>
        <li><strong>Exchange API credentials:</strong> encrypted at rest. We never see your withdraw-enabled keys; you must not provide them.</li>
        <li><strong>Payment metadata:</strong> Stripe customer/session IDs only. Card details are handled by Stripe; we never see your card number.</li>
        <li><strong>Communications:</strong> support emails and inquiries you send us.</li>
      </ul>
      <h3>Collected automatically</h3>
      <ul>
        <li>Bot configurations, trade history, portfolio values you generate on the Service.</li>
        <li>Session cookies for authentication (httpOnly, SameSite=Lax).</li>
        <li>Standard server logs (IP, user agent, timestamps) for security and abuse prevention, retained for 30 days.</li>
      </ul>
      <p>We do <strong>not</strong> use third-party analytics, tracking pixels, or advertising cookies at this time.</p>

      <h2>2. How We Use Your Information</h2>
      <ul>
        <li>To provide, secure, and improve the Service;</li>
        <li>To process payments and prevent fraud;</li>
        <li>To compute anonymized aggregate statistics (no individual user is identifiable);</li>
        <li>To communicate with you about account, billing, and updates;</li>
        <li>To comply with legal obligations and respond to lawful requests.</li>
      </ul>

      <h2>3. Sharing</h2>
      <p>We do not sell your personal information. We share data only with:</p>
      <ul>
        <li><strong>Service providers</strong> we use to operate (Stripe, MongoDB hosting, AI API providers) under contracts that restrict their use of the data to providing the Service.</li>
        <li><strong>Law enforcement</strong> when required by valid legal process.</li>
        <li><strong>An acquirer</strong> in the event of a merger or asset sale, with notice to you.</li>
      </ul>

      <h2>4. Data Security</h2>
      <p>
        We use industry-standard practices, including bcrypt password hashing, Fernet encryption for exchange API keys,
        TLS in transit, and JWT-based session tokens. No system is perfectly secure, however; you acknowledge and accept
        residual risk.
      </p>

      <h2>5. Your Rights</h2>
      <ul>
        <li><strong>Access &amp; portability:</strong> use the in-app data export to download all data we hold about you.</li>
        <li><strong>Deletion:</strong> delete your account from Settings; we will permanently remove your personal data, subject to legal retention obligations.</li>
        <li><strong>Correction:</strong> update your name and email in Settings.</li>
        <li><strong>Opt out of sale:</strong> we do not sell personal information.</li>
        <li><strong>EU/UK residents</strong> have additional GDPR rights and may contact us or your local data-protection authority.</li>
        <li><strong>California residents</strong> have CCPA rights including the rights described above.</li>
      </ul>
      <p>To exercise any right, email <a href={`mailto:${LEGAL.legalEmail}`}>{LEGAL.legalEmail}</a>.</p>

      <h2>6. Children</h2>
      <p>The Service is not directed to children under 18. We do not knowingly collect information from children.</p>

      <h2>7. International Transfers</h2>
      <p>
        We operate in the United States. If you access the Service from outside the U.S., you consent to the transfer
        of your data to the U.S. and other jurisdictions where our service providers operate.
      </p>

      <h2>8. Data Retention</h2>
      <p>
        We retain account data while your account is active. Server logs are retained for 30 days. Financial transaction
        records may be retained longer to satisfy tax and accounting obligations. Deleted-account data is purged
        permanently within 30 days, except where retention is legally required.
      </p>

      <h2>9. Changes</h2>
      <p>Material changes to this Policy will be announced via email or in-app notice prior to taking effect.</p>

      <h2>10. Contact</h2>
      <p>Email <a href={`mailto:${LEGAL.legalEmail}`}>{LEGAL.legalEmail}</a> for any privacy question, request, or complaint.</p>
    </LegalLayout>
  );
}
